import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { X, Radio, Send, Search, CheckCircle2, Phone, Loader2 } from 'lucide-react';

const LANG_OPTIONS = [
  { k: 'ta', label: 'Tamil தமிழ்' },
  { k: 'hi', label: 'Hindi हिन्दी' },
  { k: 'ne', label: 'Nepali नेपाली' },
  { k: 'kn', label: 'Kannada ಕನ್ನಡ' },
  { k: 'en', label: 'English' },
];

/**
 * ScaleBoard Radio — "Send as WhatsApp training drop" modal.
 *
 * Admin picks recipients + language → backend generates a unique tokenized
 * URL per person and WhatsApps them. Each recipient can only open THEIR
 * link. Opens, plays, and completions are tracked.
 */
export default function RadioSendModal({ sopId, sopTitle, onClose }) {
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [lang, setLang] = useState('ta');
  const [note, setNote] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  // Inline phone entry — key = recipient id being edited, value = typed digits
  const [phoneDraft, setPhoneDraft] = useState({});
  const [savingPhone, setSavingPhone] = useState('');

  useEffect(() => {
    // Unified picker — merges `users` (CEO/leaders, phone often on user doc)
    // and `people` (staff) so Dipali's own number surfaces correctly.
    api.get('/radio/recipients', { params: { limit: 500 } })
      .then((r) => setPeople(Array.isArray(r.data?.items) ? r.data.items : []))
      .catch(() => setPeople([]))
      .finally(() => setLoading(false));
  }, []);

  // Normalize phone across possible field names (phone / mobile / whatsapp / ...)
  const getPhone = (p) => (p.phone || p.mobile || p.whatsapp || p.whatsapp_number || p.contact_number || '').toString().trim();

  // Inline phone save — writes to the recipient's record and updates local
  // state so the row immediately flips from "No phone" → "•NNNN" + selectable.
  const savePhone = async (id) => {
    const raw = (phoneDraft[id] || '').trim();
    const digits = raw.replace(/\D/g, '');
    if (digits.length < 10) {
      toast.error('Phone needs at least 10 digits');
      return;
    }
    setSavingPhone(id);
    try {
      const r = await api.patch(`/radio/recipients/${id}/phone`, { phone: digits });
      setPeople((prev) => prev.map((p) => (p._id === id ? { ...p, phone: r.data.phone } : p)));
      setPhoneDraft((prev) => { const n = { ...prev }; delete n[id]; return n; });
      toast.success(`Saved · ${r.data.name} now has •${digits.slice(-4)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save phone');
    } finally {
      setSavingPhone('');
    }
  };

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return people;
    return people.filter((p) =>
      (p.name || '').toLowerCase().includes(ql) ||
      getPhone(p).includes(ql) ||
      (p.outlet_name || '').toLowerCase().includes(ql) ||
      (p.role || '').toLowerCase().includes(ql),
    );
  }, [people, q]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => setSelected(new Set(filtered.map((p) => p._id || p.id)));
  const clearAll = () => setSelected(new Set());

  const send = async () => {
    if (selected.size === 0) {
      toast.warning('Pick at least one recipient');
      return;
    }
    setSending(true);
    try {
      const r = await api.post('/radio/send', {
        sop_id: sopId,
        lang,
        person_ids: Array.from(selected),
        note: note.trim() || null,
        // Pass the admin's current origin so tokenized links resolve on the
        // same environment (preview vs scaleboard.in) instead of defaulting
        // to whatever DASHBOARD_URL env var is set to on the backend.
        origin: typeof window !== 'undefined' ? window.location.origin : null,
      });
      setResult(r.data);
      toast.success(`Sent to ${r.data.delivered} of ${r.data.total_targets}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const peopleWithPhone = filtered.filter((p) => getPhone(p)).length;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-2" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="radio-send-modal">
        <div className="p-4 border-b flex items-start justify-between" style={{ borderColor: '#E5E1DC', backgroundColor: '#F7F5F2' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#2A7B88' }}>
              <Radio size={16} style={{ color: '#FFF' }} />
            </div>
            <div>
              <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Send on Radio</p>
              <p className="text-[11px]" style={{ color: '#6B7280' }}>{sopTitle}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="radio-send-close"><X size={18} style={{ color: '#6B7280' }} /></button>
        </div>

        {result ? (
          <div className="p-6 text-center overflow-y-auto">
            {result.delivered > 0 ? (
              <CheckCircle2 size={36} className="mx-auto mb-3" style={{ color: '#2D8B5E' }} />
            ) : (
              <div className="mx-auto mb-3 w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FEF3E5' }}>
                <span style={{ color: '#D4726A', fontSize: 20 }}>!</span>
              </div>
            )}
            <p className="text-base font-semibold" style={{ color: '#1B2A4A' }}>
              {result.delivered > 0 ? 'Dropped! 🎉' : 'Links created — WhatsApp not sent'}
            </p>
            <div className="mt-3 text-xs space-y-1" style={{ color: '#6B7280' }}>
              {result.delivered > 0 && (
                <p>✅ <strong style={{ color: '#2D8B5E' }}>{result.delivered}</strong> recipients received their personalized WhatsApp link</p>
              )}
              {result.skipped_no_phone > 0 && <p>⏭ {result.skipped_no_phone} skipped (no phone number on file)</p>}
              {result.whatsapp_configured === false && (
                <div className="mt-2 p-2 rounded-md text-left" style={{ backgroundColor: '#FEF3E5', color: '#8A5A00' }} data-testid="radio-gupshup-not-configured">
                  <p className="text-[11px] font-semibold mb-0.5">⚠️ Gupshup sender not configured</p>
                  <p className="text-[10px]">
                    Tokenized links are created but WhatsApps are not dispatched yet. Set
                    <code className="mx-1 px-1 rounded" style={{ backgroundColor: '#FFF' }}>GUPSHUP_SENDER</code>
                    in <code className="px-1 rounded" style={{ backgroundColor: '#FFF' }}>backend/.env</code>
                    to your approved WhatsApp business number (E.164 digits, e.g. <code>919739908844</code>) and restart backend.
                  </p>
                </div>
              )}
              {Array.isArray(result.send_failures) && result.send_failures.length > 0 && (
                <div className="mt-2 p-2 rounded-md text-left" style={{ backgroundColor: '#FFF5F5', color: '#8A2A2A' }} data-testid="radio-send-failures">
                  <p className="text-[11px] font-semibold mb-1">{result.send_failures.length} failed to deliver</p>
                  <ul className="space-y-0.5">
                    {result.send_failures.slice(0, 5).map((f, i) => (
                      <li key={i} className="text-[10px]">• {f.name} (•{f.phone_tail}) — {f.status}{f.reason ? `: ${f.reason.slice(0, 80)}` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="pt-2">Track opens + plays in the <strong>Radio tab</strong> of Admin.</p>
            </div>
            {result.sample_link && (
              <div className="mt-4 p-3 rounded-md" style={{ backgroundColor: '#F0FAFB' }}>
                <p className="text-[10px] uppercase font-semibold mb-1" style={{ color: '#2A7B88' }}>Preview a link (works like what recipients get)</p>
                <a href={result.sample_link} target="_blank" rel="noreferrer" className="text-[11px] break-all underline" style={{ color: '#2A7B88' }}>
                  {result.sample_link}
                </a>
              </div>
            )}
            <button onClick={onClose} className="mt-5 px-4 py-2 rounded-md text-xs font-medium" style={{ backgroundColor: '#2A7B88', color: '#FFF' }}>Done</button>
          </div>
        ) : (
          <>
            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              <div>
                <label className="text-[11px] font-semibold" style={{ color: '#6B7280' }}>Language of the broadcast</label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {LANG_OPTIONS.map((o) => (
                    <button key={o.k} onClick={() => setLang(o.k)}
                      className="text-[11px] px-2.5 py-1 rounded-full border"
                      style={{
                        backgroundColor: lang === o.k ? '#2A7B88' : '#FFF',
                        color: lang === o.k ? '#FFF' : '#6B7280',
                        borderColor: lang === o.k ? '#2A7B88' : '#E5E1DC',
                      }}
                      data-testid={`radio-lang-${o.k}`}
                    >{o.label}</button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[11px] font-semibold" style={{ color: '#6B7280' }}>Optional personal note (1 line)</label>
                <input type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={120}
                  placeholder="e.g. Please listen before your shift tomorrow"
                  className="w-full mt-1 text-xs px-2 py-1.5 rounded border" style={{ borderColor: '#E5E1DC' }}
                  data-testid="radio-note-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-semibold" style={{ color: '#6B7280' }}>
                    Pick recipients {selected.size > 0 && <span style={{ color: '#2A7B88' }}>· {selected.size} selected</span>}
                  </label>
                  <div className="flex gap-1">
                    <button onClick={selectAllVisible} className="text-[10px] px-2 py-0.5 rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>Select visible</button>
                    {selected.size > 0 && <button onClick={clearAll} className="text-[10px] px-2 py-0.5 rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>Clear</button>}
                  </div>
                </div>
                <div className="relative mt-1">
                  <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: '#9CA3AF' }} />
                  <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name · phone · outlet"
                    className="w-full text-[11px] pl-7 pr-2 py-1.5 rounded border" style={{ borderColor: '#E5E1DC' }}
                    data-testid="radio-people-search"
                  />
                </div>
                <p className="text-[10px] mt-1" style={{ color: '#9CA3AF' }}>
                  {loading ? 'Loading…' : `${peopleWithPhone} of ${filtered.length} have a phone number on file`}
                </p>
                {!loading && (filtered.length - peopleWithPhone) > 2 && (
                  <div className="mt-1.5 p-2 rounded text-[10px] flex items-start gap-1.5"
                    style={{ backgroundColor: '#FEF3E5', color: '#8A5A00' }}
                    data-testid="radio-bulk-phone-hint">
                    <Phone size={11} className="shrink-0 mt-0.5" />
                    <span>
                      <b>{filtered.length - peopleWithPhone} staff missing a phone number.</b> Click
                      <span className="mx-0.5 px-1 rounded font-semibold" style={{ backgroundColor: '#FEF2F2', color: '#D4726A' }}>+ Add number</span>
                      on any row to save a number instantly — no need to leave this modal. The number is saved to their staff record for every future Radio drop.
                    </span>
                  </div>
                )}

                <div className="mt-2 border rounded-md max-h-64 overflow-y-auto" style={{ borderColor: '#E5E1DC' }}>
                  {filtered.map((p) => {
                    const id = p._id || p.id;
                    const checked = selected.has(id);
                    const phone = getPhone(p);
                    const hasPhone = !!phone;
                    const editing = phoneDraft[id] !== undefined;
                    return (
                      <div key={id} className="flex items-center gap-2 px-2 py-1.5 border-b hover:bg-[#F7F5F2]"
                        style={{ borderColor: '#F3F0EB', opacity: hasPhone || editing ? 1 : 0.75 }}
                        data-testid={`radio-person-${id}`}
                      >
                        <input type="checkbox" checked={checked} onChange={() => toggle(id)} disabled={!hasPhone}
                          className="cursor-pointer" />
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => hasPhone && toggle(id)}>
                          <p className="text-xs font-medium truncate" style={{ color: '#1B2A4A' }}>{p.name}</p>
                          <p className="text-[10px] truncate" style={{ color: '#9CA3AF' }}>{p.role || '—'} · {p.outlet_name || '—'}</p>
                        </div>
                        {hasPhone ? (
                          <span className="text-[10px] inline-flex items-center gap-0.5" style={{ color: '#2D8B5E' }}>
                            <Phone size={10} /> {phone.length > 4 ? `•${phone.slice(-4)}` : phone}
                          </span>
                        ) : editing ? (
                          <div className="flex items-center gap-1 shrink-0">
                            <input
                              type="tel"
                              value={phoneDraft[id]}
                              onChange={(e) => setPhoneDraft((prev) => ({ ...prev, [id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') savePhone(id); if (e.key === 'Escape') setPhoneDraft((prev) => { const n = { ...prev }; delete n[id]; return n; }); }}
                              placeholder="97399 08844"
                              autoFocus
                              className="text-[10px] w-[110px] px-1.5 py-0.5 rounded border"
                              style={{ borderColor: '#2A7B88' }}
                              data-testid={`radio-phone-input-${id}`}
                            />
                            <button
                              onClick={() => savePhone(id)}
                              disabled={savingPhone === id}
                              className="text-[10px] px-1.5 py-0.5 rounded font-semibold disabled:opacity-60"
                              style={{ backgroundColor: '#2A7B88', color: '#FFF' }}
                              data-testid={`radio-phone-save-${id}`}
                            >{savingPhone === id ? '…' : 'Save'}</button>
                            <button
                              onClick={() => setPhoneDraft((prev) => { const n = { ...prev }; delete n[id]; return n; })}
                              className="text-[10px] px-1.5 py-0.5 rounded border"
                              style={{ borderColor: '#E5E1DC', color: '#9CA3AF' }}
                            >✕</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setPhoneDraft((prev) => ({ ...prev, [id]: '' }))}
                            className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border shrink-0"
                            style={{ borderColor: '#D4726A', color: '#D4726A', backgroundColor: '#FEF2F2' }}
                            data-testid={`radio-phone-add-${id}`}
                          >
                            <Phone size={10} /> + Add number
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {!loading && filtered.length === 0 && (
                    <p className="text-center text-xs py-4" style={{ color: '#9CA3AF' }}>No matching staff</p>
                  )}
                </div>
              </div>
            </div>

            <div className="p-3 border-t flex items-center justify-between gap-2" style={{ borderColor: '#E5E1DC' }}>
              <p className="text-[10px]" style={{ color: '#9CA3AF' }}>🔒 Each recipient gets a unique link. Forwards auto-flag.</p>
              <button onClick={send} disabled={sending || selected.size === 0}
                className="px-4 py-2 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                style={{ backgroundColor: '#2A7B88', color: '#FFF' }}
                data-testid="radio-send-btn"
              >
                {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                {sending ? 'Sending…' : `Send to ${selected.size}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
